import { SOSSIETY_RED, SOSSIETY_RED_HOVER } from '@/styles/brand';
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Plus, Trash2, Clock } from 'lucide-react'
import { useI18n } from '@/contexts/I18nContext'
import { useInstructorScheduleIntervals } from '@/features/calendar/hooks/useInstructorScheduleIntervals'
import type { ScheduleInterval } from '@/features/calendar/hooks/useInstructorScheduleIntervals'
import { TopNavigation } from '@/components/layout/TopNavigation'
import { TIME_OPTIONS } from '@/utils/timeFormatting'

// ─── Design tokens ────────────────────────────────────────────────────────────

const C = {
  bg:      '#160C0C',
  surface: '#281B1B',
  border:  '#FFFFFF0D',
  muted:   '#8D8D8D',
  accent:  SOSSIETY_RED,
  danger:  '#EF4444',
}

// ─── Constants ────────────────────────────────────────────────────────────────

const DAYS = [
  { key: 'mon', short: 'M', label: 'Monday' },
  { key: 'tue', short: 'T', label: 'Tuesday' },
  { key: 'wed', short: 'W', label: 'Wednesday' },
  { key: 'thu', short: 'T', label: 'Thursday' },
  { key: 'fri', short: 'F', label: 'Friday' },
  { key: 'sat', short: 'S', label: 'Saturday' },
  { key: 'sun', short: 'S', label: 'Sunday' },
] as const

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmt = (t: string) => t.slice(0, 5)

function ampmTo24h(ampm: string): string | null {
  const match = ampm.trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i)
  if (!match) return null
  let h = parseInt(match[1])
  const m = match[2]
  const period = match[3].toUpperCase()
  if (period === 'AM' && h === 12) h = 0
  if (period === 'PM' && h !== 12) h += 12
  return `${String(h).padStart(2, '0')}:${m}`
}

function diffMin24(start: string, end: string) {
  const [sh, sm] = start.split(':').map(Number)
  const [eh, em] = end.split(':').map(Number)
  return (eh * 60 + em) - (sh * 60 + sm)
}

// ─── AddIntervalForm ──────────────────────────────────────────────────────────

interface AddIntervalFormProps {
  onAdd:    (start: string, end: string) => Promise<void>
  isAdding: boolean
  addError: string | null
  onCancel: () => void
  t:        (key: string, vars?: Record<string, unknown>) => string
}

const TimeField = ({
  id,
  label,
  value,
  onChange,
}: {
  id: string
  label: string
  value: string
  onChange: (v: string) => void
}) => (
  <div className="flex-1">
    <p className="text-xs mb-1.5" style={{ color: C.muted }}>{label}</p>
    <div className="relative">
      <input
        id={id}
        type="text"
        list={`${id}-list`}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder="8:00 AM"
        autoComplete="off"
        className="w-full rounded-lg px-3 py-2.5 pr-9 text-sm text-white border focus:outline-none"
        style={{ backgroundColor: '#1E1414', borderColor: C.border }}
      />
      <datalist id={`${id}-list`}>
        {TIME_OPTIONS.map(opt => <option key={opt} value={opt} />)}
      </datalist>
      <Clock className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none" style={{ color: C.muted }} />
    </div>
  </div>
)

const AddIntervalForm = ({ onAdd, isAdding, addError, onCancel, t }: AddIntervalFormProps) => {
  const [start, setStart] = useState('8:00 AM')
  const [end,   setEnd]   = useState('10:00 AM')

  const start24 = ampmTo24h(start)
  const end24   = ampmTo24h(end)
  const diff    = start24 && end24 ? diffMin24(start24, end24) : -1
  const valid   = !!start24 && !!end24 && diff >= 60

  return (
    <div
      className="rounded-xl p-4 space-y-3 border"
      style={{ backgroundColor: C.surface, borderColor: C.border }}
    >
      <div className="flex gap-3">
        <TimeField
          id="interval-start"
          label={t('instructor_tools.start_time')}
          value={start}
          onChange={setStart}
        />
        <TimeField
          id="interval-end"
          label={t('instructor_tools.end_time')}
          value={end}
          onChange={setEnd}
        />
      </div>

      {start24 && end24 && diff < 60 && diff >= 0 && (
        <p className="text-xs" style={{ color: C.danger }}>
          {t('instructor_tools.min_1h')}
        </p>
      )}
      {start24 && end24 && diff >= 60 && (
        <p className="text-xs" style={{ color: C.muted }}>
          {t('instructor_tools.interval_duration', { diff })}
        </p>
      )}
      {addError && (
        <p className="text-xs" style={{ color: C.danger }}>{addError}</p>
      )}

      <div className="flex gap-2">
        <button
          onClick={onCancel}
          className="flex-1 rounded-full py-2 text-sm font-medium border"
          style={{ color: C.muted, borderColor: C.border }}
        >
          {t('instructor_tools.cancel')}
        </button>
        <button
          disabled={!valid || isAdding}
          onClick={() => valid && start24 && end24 && onAdd(start24, end24)}
          className="flex-1 rounded-full py-2 text-sm font-semibold text-white disabled:opacity-40 transition-opacity"
          style={{ backgroundColor: C.accent }}
        >
          {isAdding ? t('instructor_tools.adding') : t('instructor_tools.add')}
        </button>
      </div>
    </div>
  )
}

// ─── IntervalRow ──────────────────────────────────────────────────────────────

const IntervalRow = ({
  interval,
  onDelete,
  isDeleting,
}: {
  interval:   ScheduleInterval
  onDelete:   () => void
  isDeleting: boolean
}) => (
  <div
    className="flex items-center justify-between rounded-xl px-4 py-3 border"
    style={{ backgroundColor: C.surface, borderColor: C.border }}
  >
    <p className="text-sm font-medium text-white">
      {fmt(interval.start_time)} – {fmt(interval.end_time)}
    </p>
    <button
      onClick={onDelete}
      disabled={isDeleting}
      className="p-1 transition-opacity disabled:opacity-40 hover:opacity-70"
      aria-label="Delete interval"
    >
      <Trash2 className="w-4 h-4" style={{ color: C.danger }} />
    </button>
  </div>
)

// ─── Page ─────────────────────────────────────────────────────────────────────

const Schedule = () => {
  const navigate = useNavigate()
  const { t }    = useI18n()

  const {
    intervals,
    isLoading,
    addInterval, isAdding, addError,
    deleteInterval, isDeleting,
  } = useInstructorScheduleIntervals()

  const [selectedDay, setSelectedDay] = useState<string>('mon')
  const [showAddForm, setShowAddForm] = useState(false)

  const dayIntervals = intervals
    .filter(iv => iv.day_of_week === selectedDay)
    .sort((a, b) => a.start_time.localeCompare(b.start_time))

  const handleAddInterval = async (start: string, end: string) => {
    await addInterval({ day_of_week: selectedDay, start_time: start, end_time: end })
    setShowAddForm(false)
  }

  return (
    <div className="min-h-screen text-white" style={{ backgroundColor: C.bg }}>
      <TopNavigation />

      <div className="pt-16">
        {/* Header */}
        <div className="relative flex items-center px-4 py-3.5 border-b border-[#281B1B]">
          <button
            onClick={() => navigate(-1)}
            className="p-1 text-white/80 hover:text-white transition-colors z-10">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="absolute left-1/2 -translate-x-1/2 text-base font-semibold truncate max-w-[60%]">
            {t('instructor_tools.edit_schedule')}
          </h1>
        </div>

      <p className="px-5 text-xs mt-4 mb-5" style={{ color: C.muted }}>
        {t('instructor_tools.schedule_subtitle')}
      </p>

      {/* Day selector */}
      <div className="flex gap-2 px-5 mb-5">
        {DAYS.map(day => (
          <button
            key={day.key}
            onClick={() => { setSelectedDay(day.key); setShowAddForm(false) }}
            className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-semibold transition-colors flex-shrink-0"
            style={
              selectedDay === day.key
                ? { backgroundColor: C.accent, color: '#fff' }
                : { backgroundColor: C.surface, color: C.muted }
            }
          >
            {day.short}
          </button>
        ))}
      </div>

      <div className="px-5 space-y-2 mb-4">
        {/* Interval list */}
        {isLoading ? (
          <p className="text-sm" style={{ color: C.muted }}>
            {t('instructor_tools.loading')}
          </p>
        ) : dayIntervals.length === 0 && !showAddForm ? (
          <p className="text-sm" style={{ color: C.muted }}>
            {t('instructor_tools.no_intervals')}
          </p>
        ) : (
          dayIntervals.map(iv => (
            <IntervalRow
              key={iv.id}
              interval={iv}
              onDelete={() => deleteInterval(iv.id)}
              isDeleting={isDeleting}
            />
          ))
        )}

        {/* Add form or Add button */}
        {showAddForm ? (
          <AddIntervalForm
            onAdd={handleAddInterval}
            isAdding={isAdding}
            addError={addError}
            onCancel={() => setShowAddForm(false)}
            t={t}
          />
        ) : (
          <button
            onClick={() => setShowAddForm(true)}
            className="flex items-center gap-2 text-sm font-medium pt-1 transition-opacity"
            style={{ color: C.accent }}
          >
            <Plus className="w-4 h-4" />
            {t('instructor_tools.add_interval')}
          </button>
        )}
      </div>

      </div>
    </div>
  )
}

export default Schedule
